import { Supabase } from '../Utils/supabase.config';

// Track that a user has read an announcement
export const markAnnouncementAsRead = async (userId, announcementId) => {
  try {
    const { data, error } = await Supabase
      .from('announcement_reads')
      .upsert({
        user_id: userId,
        announcement_id: announcementId,
        read_at: new Date().toISOString()
      }, {
        onConflict: 'user_id,announcement_id'
      });

    if (error) {
      console.error('Error marking announcement as read:', error);
      return { success: false, msg: 'Could not mark announcement as read' };
    }

    return { success: true, data };
  } catch (error) {
    console.error('Error marking announcement as read:', error);
    return { success: false, msg: 'Could not mark announcement as read' };
  }
};

// Get unread announcement count for a user
export const getUnreadAnnouncementCount = async (userId) => {
  try {
    // First, get all announcement posts
    const { data: announcements, error: announcementsError } = await Supabase
      .from('posts')
      .select('id')
      .eq('is_announcement', true)
      .order('created_at', { ascending: false });

    if (announcementsError) {
      console.error('Error fetching announcements:', announcementsError);
      return { success: false, msg: 'Could not fetch announcements' };
    }

    if (!announcements || announcements.length === 0) {
      return { success: true, data: 0 };
    }

    // Get read announcements for this user
    const announcementIds = announcements.map(a => a.id);
    const { data: readAnnouncements, error: readError } = await Supabase
      .from('announcement_reads')
      .select('announcement_id')
      .eq('user_id', userId)
      .in('announcement_id', announcementIds);

    if (readError) {
      console.error('Error fetching read announcements:', readError);
      return { success: false, msg: 'Could not fetch read announcements' };
    }

    const readIds = new Set(readAnnouncements?.map(r => r.announcement_id) || []);
    const unreadCount = announcements.filter(a => !readIds.has(a.id)).length;

    return { success: true, data: unreadCount };
  } catch (error) {
    console.error('Error getting unread announcement count:', error);
    return { success: false, msg: 'Could not get unread announcement count' };
  }
};

// Get unread announcements for a user
export const getUnreadAnnouncements = async (userId, limit = 10) => {
  try {
    // Get all announcement posts with details
    const { data: announcements, error: announcementsError } = await Supabase
      .from('posts')
      .select(`
        id,
        body,
        file,
        created_at,
        user_id,
        community_id,
        is_pinned,
        is_announcement,
        user:users(id, name, image),
        community:communities(id, name, profile_image),
        postLikes:post_likes(userId),
        comments:comments(count)
      `)
      .eq('is_announcement', true)
      .order('created_at', { ascending: false })
      .limit(limit);

    if (announcementsError) {
      console.error('Error fetching announcements:', announcementsError);
      return { success: false, msg: 'Could not fetch announcements' };
    }

    if (!announcements || announcements.length === 0) {
      return { success: true, data: [] };
    }

    // Get read announcements for this user
    const announcementIds = announcements.map(a => a.id);
    const { data: readAnnouncements, error: readError } = await Supabase
      .from('announcement_reads')
      .select('announcement_id')
      .eq('user_id', userId)
      .in('announcement_id', announcementIds);

    if (readError) {
      console.error('Error fetching read announcements:', readError);
      return { success: false, msg: 'Could not fetch read announcements' };
    }

    const readIds = new Set(readAnnouncements?.map(r => r.announcement_id) || []);
    const unreadAnnouncements = announcements.filter(a => !readIds.has(a.id));

    return { success: true, data: unreadAnnouncements };
  } catch (error) {
    console.error('Error getting unread announcements:', error);
    return { success: false, msg: 'Could not get unread announcements' };
  }
};

// Mark all announcements as read for a user
export const markAllAnnouncementsAsRead = async (userId) => {
  try {
    // Get all announcement IDs
    const { data: announcements, error: announcementsError } = await Supabase
      .from('posts')
      .select('id')
      .eq('is_announcement', true);

    if (announcementsError) {
      console.error('Error fetching announcements:', announcementsError);
      return { success: false, msg: 'Could not fetch announcements' };
    }

    if (!announcements || announcements.length === 0) {
      return { success: true, data: [] };
    }

    // Mark all as read
    const readRecords = announcements.map(announcement => ({
      user_id: userId,
      announcement_id: announcement.id,
      read_at: new Date().toISOString()
    }));

    const { data, error } = await Supabase
      .from('announcement_reads')
      .upsert(readRecords, {
        onConflict: 'user_id,announcement_id'
      });

    if (error) {
      console.error('Error marking all announcements as read:', error);
      return { success: false, msg: 'Could not mark all announcements as read' };
    }

    return { success: true, data };
  } catch (error) {
    console.error('Error marking all announcements as read:', error);
    return { success: false, msg: 'Could not mark all announcements as read' };
  }
};
